/**
 * customer-types — the ONE way this app reads the company's customer-type
 * catalogue and resolves a customer's assignment to a label.
 *
 * Three rules from the wiring kit, made structural rather than restated at
 * every call site:
 *
 * - **Read to the advertised total.** `listCustomerTypes` pages at most 200 per
 *   call. `loadAllCustomerTypes` keeps reading until `total` is covered, treats
 *   an empty page before that as an INCOMPLETE load (thrown, never a silently
 *   short list), and de-duplicates by id in case rows moved between pages.
 * - **Archived is still a name.** Resolution includes archived rows, because an
 *   existing assignment may point at one. Only NEW assignments are restricted
 *   to active rows — that is the caller's filter (`active`), not the reader's.
 * - **Null is Unassigned; an unresolved non-null id is Unknown type.** The two
 *   render differently and the original id is preserved for the assignment
 *   conflict check (`expected_customer_type_id`).
 *
 * Scope: `customer_types.*` are `session-company` scoped. This app's session
 * shell has no company axis, so the catalogue is keyed to the CONFIRMED tenant
 * (purged on `onTenantSwitching`, re-read on `onTenantChange`), the same
 * pattern EthosSyncSection uses. A response from a previous scope is dropped.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, listCustomerTypes } from "@v0y4ge/api-client";
import type { CustomerTypeDto } from "@v0y4ge/api-contracts";
import { getConfirmedTenantSlug, onTenantChange, onTenantSwitching, useTenant } from "../../shell/access";

export const TYPE_PAGE_SIZE = 200;

export class IncompleteTypeListError extends Error {
  constructor(public readonly loaded: number, public readonly total: number) {
    super(`Customer type list is incomplete: ${loaded} of ${total} loaded.`);
    this.name = "IncompleteTypeListError";
  }
}

export const isApiError = (e: unknown): e is ApiError =>
  e instanceof ApiError || (typeof e === "object" && e !== null && "apiErrorBrand" in e && "code" in e);
export const codeOf = (e: unknown): string => (isApiError(e) ? e.code : "");
export const statusOf = (e: unknown): number => (isApiError(e) ? e.status : 0);
export const isDenied = (e: unknown): boolean => statusOf(e) === 403 || statusOf(e) === 401;
export const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/**
 * Every type the company has, across all pages. `status` narrows server-side.
 * Throws `IncompleteTypeListError` when a page comes back empty before the
 * advertised total is covered — the caller shows a retry, never a short list.
 */
export async function loadAllCustomerTypes(query: { status?: "active" | "archived"; search?: string } = {}): Promise<CustomerTypeDto[]> {
  const seen = new Map<string, CustomerTypeDto>();
  let page = 1;
  let total = Infinity;
  // Hard ceiling so a server that keeps reporting a moving total cannot loop
  // this forever: one extra page beyond what the first total implied.
  let maxPages = Infinity;
  while (seen.size < total && page <= maxPages) {
    const res = await listCustomerTypes({ page, page_size: TYPE_PAGE_SIZE, ...query });
    total = res.total;
    if (maxPages === Infinity) maxPages = Math.ceil(total / TYPE_PAGE_SIZE) + 1;
    if (res.items.length === 0) {
      if (seen.size < total) throw new IncompleteTypeListError(seen.size, total);
      break;
    }
    for (const row of res.items) seen.set(row.id, row);
    page += 1;
  }
  if (seen.size < total) throw new IncompleteTypeListError(seen.size, total);
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

export type TypeResolution =
  | { kind: "unassigned"; id: null; name: null; type: null }
  | { kind: "unknown"; id: string; name: null; type: null }
  | { kind: "active" | "archived"; id: string; name: string; type: CustomerTypeDto };

export function resolveType(byId: ReadonlyMap<string, CustomerTypeDto>, id: string | null | undefined): TypeResolution {
  if (id == null || id === "") return { kind: "unassigned", id: null, name: null, type: null };
  const hit = byId.get(id);
  if (!hit) return { kind: "unknown", id, name: null, type: null };
  return { kind: hit.status === "archived" ? "archived" : "active", id, name: hit.name, type: hit };
}

export interface TypeCatalog {
  types: CustomerTypeDto[];
  byId: ReadonlyMap<string, CustomerTypeDto>;
  active: CustomerTypeDto[];
  loading: boolean;
  /** Non-null when the last read failed. `denied` distinguishes a permission
   * refusal (no retry will help) from a transport/incomplete failure. */
  error: string | null;
  denied: boolean;
  /** True once at least one COMPLETE read landed for the current scope. */
  loaded: boolean;
  reload: () => Promise<void>;
}

/**
 * The company's full type catalogue (active AND archived), tenant-scoped.
 * `enabled=false` (no `customer_types.read`) reads nothing and reports
 * `denied` so the host keeps a usable list without names.
 */
export function useCustomerTypeCatalog(enabled: boolean): TypeCatalog {
  const tenant = useTenant();
  const scopeKey = tenant?.slug ?? "";
  const generation = useRef(0);
  const requestScope = useRef<string | null>(null);
  const [types, setTypes] = useState<CustomerTypeDto[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(!enabled);
  const [loaded, setLoaded] = useState(false);

  const isCurrent = useCallback((req: { generation: number; scope: string | null }) =>
    req.scope !== null && req.generation === generation.current && req.scope === requestScope.current && req.scope === getConfirmedTenantSlug(), []);

  const read = useCallback(async () => {
    if (!enabled) { setDenied(true); setLoading(false); return; }
    requestScope.current = getConfirmedTenantSlug();
    const req = { generation: generation.current, scope: requestScope.current };
    if (!isCurrent(req)) return;
    setLoading(true);
    try {
      const all = await loadAllCustomerTypes();
      if (!isCurrent(req)) return;
      setTypes(all); setError(null); setDenied(false); setLoaded(true);
    } catch (e) {
      if (!isCurrent(req)) return;
      setDenied(isDenied(e));
      setError(messageOf(e));
    } finally { if (isCurrent(req)) setLoading(false); }
  }, [enabled, isCurrent]);

  useEffect(() => {
    const clear = () => {
      generation.current += 1;
      requestScope.current = null;
      setTypes([]); setError(null); setLoaded(false); setDenied(!enabled); setLoading(enabled);
    };
    clear();
    const offSwitching = onTenantSwitching(clear);
    const offChange = onTenantChange(() => { void read(); });
    void read();
    return () => { generation.current += 1; requestScope.current = null; offSwitching(); offChange(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scopeKey]);

  const byId = useMemo(() => new Map(types.map((x) => [x.id, x] as const)), [types]);
  const active = useMemo(() => types.filter((x) => x.status === "active"), [types]);
  return { types, byId, active, loading, error, denied, loaded, reload: read };
}
